/**
 * Sound table: every sound id the game plays → which samples, bus, priority, limits and synth
 * fallback it uses. One place to tune the mix.
 *
 * One palette: every sample comes from the TomMusic "Free Fantasy 200+ SFX Pack" (directly, or
 * derived from it: steel chimes / jingles from the sword rings, coins from the key ring, lightning
 * from parry snaps + fire sizzle), except the creature voices (rubberduck, CC0). All clips go
 * through one offline chain (scripts/audio/build.mjs): same EQ / compression, same edits, and a
 * loudness target per role, so `gain` here stays 1 unless a sound reuses another's clip.
 *
 *  - `sample`: base clip id in manifest.gen.ts; its variants are "<sample>#0..n" (picked at random,
 *    never the same one twice in a row).
 *  - `synth`: recipe id (recipes.ts), played only while the sprite is not loaded (or failed). No
 *    synth is layered under samples any more (`layer` stays for callers that want one).
 *  - `bus`: 'sfx' (combat / world, "Combat volume"), 'ui', 'amb' or 'music'.
 *  - `prio`: voice priority (higher survives the limiter); `max`: live voices of this id;
 *    `gap`: minimum seconds between two plays (rate limit, e.g. UI errors).
 *  - `pitch` / `vary`: random pitch (± fraction) and gain (± fraction) per play, tuned per category
 *    (R below): tonal cues (chimes, stingers) barely move, footsteps and swings move most.
 *  - `duck`: playing it (as your own / a critical cue) ducks other units' SFX and the ambience.
 *  - `group`: which sprite carries it ('ui' + 'meta' + 'combat' load after the first gesture;
 *    'combat' is also requested when a run's renderer starts).
 */
import { AUDIO_MANIFEST } from './manifest.gen';
import type { BusId } from './mixer';

export interface SoundDef {
  bus: BusId;
  prio: number;
  sample?: string;
  synth?: string;
  layer?: number;
  gain?: number;
  max?: number;
  gap?: number;
  pitch?: number;
  vary?: number;
  duck?: number;
  group?: 'combat' | 'ui' | 'meta';
}

/** Per-category randomisation (pitch ± fraction, gain ± fraction). */
const R = {
  hit: { pitch: 0.04, vary: 0.08 },
  whoosh: { pitch: 0.06, vary: 0.1 },
  spell: { pitch: 0.03, vary: 0.08 },
  tonal: { pitch: 0.012, vary: 0.05 },
  creature: { pitch: 0.07, vary: 0.1 },
  world: { pitch: 0.03, vary: 0.08 },
  /** Footsteps / jumps: ±6% pitch, ±0.22 gain (≈ +1.7 / -2.2 dB), never the same variant twice running. */
  step: { pitch: 0.06, vary: 0.22 },
  ui: { pitch: 0.025, vary: 0.05 },
  fixed: { pitch: 0, vary: 0.03 },
} as const;
type Feel = keyof typeof R;

/** A combat / world sound: sample `id` (default: same name), synth fallback when a recipe exists. */
const fx = (prio: number, feel: Feel, o: Partial<SoundDef> = {}): SoundDef => ({ bus: 'sfx', prio, group: 'combat', ...R[feel], ...o });
const both = (id: string, prio: number, feel: Feel, o: Partial<SoundDef> = {}): SoundDef => fx(prio, feel, { sample: id, synth: id, ...o });
const smp = (id: string, prio: number, feel: Feel, o: Partial<SoundDef> = {}): SoundDef => fx(prio, feel, { sample: id, ...o });
const ui = (id: string, o: Partial<SoundDef> = {}): SoundDef => ({ bus: 'ui', prio: 4, group: 'ui', sample: id, ...R.ui, ...o });
const meta = (id: string, o: Partial<SoundDef> = {}): SoundDef => ({ bus: 'ui', prio: 5, group: 'meta', sample: id, ...R.tonal, ...o });
const step = (surface: string): SoundDef => fx(0, 'step', { sample: `step.${surface}`, synth: 'step', max: 4 });

export const SOUNDS: Readonly<Record<string, SoundDef>> = {
  // --- melee ---------------------------------------------------------------------------
  swing: both('swing', 2, 'whoosh', { max: 3 }),
  'swing.heavy': both('swing.heavy', 2, 'whoosh', { max: 2 }),
  'hit.blade': both('hit.blade', 4, 'hit'),
  'hit.blade.heavy': both('hit.blade.heavy', 5, 'hit'),
  'hit.blunt': both('hit.blunt', 5, 'hit'),
  'hit.shield': both('hit.shield', 5, 'hit'),
  'hit.earth': both('hit.earth', 5, 'hit'),
  'hit.execute': both('hit.execute', 6, 'hit', { pitch: 0.03 }),
  // --- bow --------------------------------------------------------------------------------
  'bow.shot': smp('bow.shot', 2, 'whoosh', { max: 3 }),
  'hit.arrow': smp('hit.arrow', 4, 'hit', { synth: 'hit.blade' }),
  // --- auto-attacks (WoW-style white swings; the director / sim may play these) -------------
  /** Main-hand white swing: the sword whoosh. */
  'auto.swing': smp('swing', 1, 'whoosh', { synth: 'swing', max: 3 }),
  /** Off-hand white swing: a lighter, quicker whoosh. */
  'auto.offhand': smp('swing.light', 1, 'whoosh', { synth: 'swing', max: 3 }),
  /** White melee hit landing. */
  'auto.hit': smp('hit.blade', 3, 'hit', { synth: 'hit.blade', gain: 0.85 }),
  /** Auto Shot release (bow twang + whoosh) and the arrow landing. */
  'auto.shot': smp('bow.shot', 1, 'whoosh', { max: 3 }),
  'auto.shotHit': smp('hit.arrow', 3, 'hit', { synth: 'hit.blade', gain: 0.85 }),
  // --- spells by school --------------------------------------------------------------------
  'hit.lightning': both('hit.lightning', 4, 'spell'),
  'hit.lightning.big': both('hit.lightning.big', 6, 'spell', { max: 2 }),
  chain: both('chain', 3, 'spell'),
  'hit.fire': both('hit.fire', 5, 'spell'),
  'hit.fire.small': both('hit.fire.small', 3, 'spell'),
  'cast.fire': both('cast.fire', 2, 'spell'),
  'hit.holy': both('hit.holy', 4, 'tonal'),
  'hit.holy.big': both('hit.holy.big', 5, 'tonal'),
  'hit.shadow': both('hit.shadow', 4, 'spell'),
  'hit.arcane': both('hit.arcane', 4, 'spell'),
  'hit.venom': both('hit.venom', 3, 'spell'),
  'hit.frost': both('hit.frost', 4, 'spell'),
  // --- support -------------------------------------------------------------------------------
  heal: both('heal', 2, 'tonal', { max: 2 }),
  'heal.big': both('heal.big', 3, 'tonal'),
  nova: both('nova', 3, 'spell'),
  'cast.holy': both('cast.holy', 2, 'tonal'),
  'shield.up': both('shield.up', 3, 'spell'),
  'shield.absorb': smp('shield.absorb', 2, 'hit', { gap: 0.25, max: 1 }),
  dispel: smp('dispel', 3, 'tonal', { gap: 0.2 }),
  // --- moments -------------------------------------------------------------------------------
  crit: both('crit', 6, 'hit', { pitch: 0.03 }),
  kill: both('kill', 6, 'hit'),
  interrupt: both('interrupt', 7, 'hit', { pitch: 0.025 }),
  proc: both('proc', 6, 'fixed'),
  'star.burst': fx(6, 'spell', { sample: 'hit.lightning.big', synth: 'nova', max: 2 }),
  'star.launch': fx(3, 'spell', { sample: 'cast.charge.storm', synth: 'nova', max: 1 }),
  stealth: smp('dodge', 3, 'whoosh', { gain: 0.6, max: 1 }),
  'trap.spring': smp('trap.spring', 4, 'hit', { synth: 'chain', max: 2 }),
  // --- +specs: specialisation impacts (TomMusic-derived clips reused; feel/table.ts SPEC_FEEL) ---
  /** Ironclad: a plate riveted on (a shield clang, lighter). */
  'spec.plate': smp('hit.shield', 4, 'hit', { synth: 'hit.shield', gain: 0.8 }),
  /** Ironclad: Shrapnel Burst (the earth-crack slam). */
  'spec.shrapnel': smp('hit.earth', 6, 'hit', { synth: 'hit.earth', max: 2 }),
  /** Ravager: Twin Reaping's flurry (heavy blade, rate-limited per hit). */
  'spec.reap': smp('hit.blade.heavy', 5, 'hit', { synth: 'hit.blade.heavy', max: 3, gap: 0.05 }),
  /** Bloodreaver: Bloodwrench / Final Draught (the execute crunch). */
  'spec.drink': smp('hit.execute', 6, 'hit', { synth: 'hit.execute', max: 2 }),
  /** Bloodreaver: deliberate self-harm (a soft cut). */
  'spec.bleed': smp('hit.blade', 2, 'hit', { synth: 'hit.blade', gain: 0.55, max: 1, gap: 0.2 }),
  // +mage-specs (TomMusic-derived clips reused).
  /** Stormcaller: Skybreak venting the whole charge (the big thunder crack). */
  'spec.skybreak': smp('hit.lightning.big', 7, 'spell', { synth: 'hit.lightning.big', max: 2 }),
  /** Stormcaller: the Overvolt burn / a static hit (a short crackle). */
  'spec.static': smp('hit.lightning', 3, 'spell', { synth: 'hit.lightning', gain: 0.7, max: 2, gap: 0.08 }),
  /** Rimecaller: a fractured Hoarlance (the frost impact, heavier). */
  'spec.shatter': smp('hit.frost', 6, 'spell', { synth: 'hit.frost', gain: 1.15, max: 2 }),
  /** Rimecaller: a freeze snapping shut (rings, the glacier's shatter). */
  'spec.freeze': smp('hit.frost', 4, 'spell', { synth: 'nova', gain: 0.85, max: 2 }),
  /** Cinderborn: Cinderfall's crater (fire over an earth thump). */
  'spec.meteor': smp('hit.fire', 7, 'spell', { synth: 'hit.earth', max: 2 }),
  /** Cinderborn: paying in health (a soft sizzle, rate-limited). */
  'spec.selfburn': smp('hit.fire.small', 2, 'spell', { synth: 'hit.fire.small', gain: 0.5, max: 1, gap: 0.25 }),
  /** Cinderborn: Ashrise, the phoenix (the big heal swell). */
  'spec.rise': smp('heal.big', 7, 'fixed', { synth: 'heal.big', max: 1 }),
  // +rogue-specs (TomMusic-derived clips reused).
  /** Shadowdancer: Umbral Finale / Gloamstrike (the shadow strike, heavier). */
  'spec.umbral': smp('hit.shadow', 5, 'hit', { synth: 'hit.shadow', max: 2 }),
  /** Shadowdancer: a Silhouette's echo (the same strike, softer, a beat later). */
  'spec.echo': smp('hit.shadow', 3, 'hit', { synth: 'hit.shadow', gain: 0.6, max: 2, gap: 0.08 }),
  /** Mirage: Return Blow / Phantom Lure (mirror-glass ring). */
  'spec.mirror': smp('hit.arcane', 4, 'hit', { synth: 'hit.arcane', gain: 0.85, max: 2 }),
  /** Venomwright: Catalyst / Catalysis (the toxic burst). */
  'spec.catalyst': smp('hit.venom', 6, 'hit', { synth: 'hit.venom', max: 2 }),
  // +acolyte-specs (TomMusic-derived clips reused).
  /** Lightbinder: Triune Ray's three bolts (a lighter holy hit, rate-limited). */
  'spec.ray': smp('hit.holy', 3, 'spell', { synth: 'hit.holy', gain: 0.75, max: 2, gap: 0.08 }),
  /** Lightbinder: a ward goes up (the shield chime). */
  'spec.ward': smp('shield.up', 4, 'spell', { synth: 'shield.up', max: 2 }),
  /** Lightbinder: an Absolution heal on a marked ally (a soft heal chime, rate-limited). */
  'spec.absolve': smp('heal', 2, 'spell', { synth: 'heal', gain: 0.45, max: 1, gap: 0.3 }),
  /** Hemomancer: blood magic landing (the shadow hit, wetter and lower). */
  'spec.blood': smp('hit.shadow', 4, 'spell', { synth: 'hit.shadow', gain: 0.9, max: 2 }),
  /** Hemomancer: Martyr (the big heal swell, taken onto yourself). */
  'spec.martyr': smp('heal.big', 6, 'fixed', { synth: 'heal.big', max: 1 }),
  /** Heretic: a curse sinking in (a soft arcane hiss). */
  'spec.curse': smp('hit.arcane', 2, 'spell', { synth: 'hit.arcane', gain: 0.6, max: 2, gap: 0.1 }),
  /** Heretic: Soulflay (the shadow strike, heavier). */
  'spec.soulflay': smp('hit.shadow', 6, 'spell', { synth: 'hit.shadow', gain: 1.15, max: 2 }),
  /** Heretic: the Hollow One claws through (the earth-crack slam). */
  'spec.hollow': smp('hit.earth', 7, 'spell', { synth: 'hit.earth', max: 1 }),
  // +ranger-specs (TomMusic clips reused). Deadeye: the trap's snap is the gunshot (a breaking crack for the big spray), the lock's
  // click-clack the cylinder, the proc chime the perfect reload.
  'spec.gunshot': smp('trap.spring', 4, 'hit', { synth: 'hit.blunt', gain: 0.8, max: 3 }),
  'spec.gunshot.fan': smp('object.break', 6, 'hit', { synth: 'hit.blunt', max: 3, gap: 0.04 }),
  'spec.ricochet': smp('hit.shield', 4, 'hit', { synth: 'hit.shield', gain: 0.6, max: 3 }),
  'spec.reload': smp('lock.unlock', 4, 'fixed', { max: 1, gap: 0.1 }),
  'spec.perfect': smp('proc', 6, 'fixed', { synth: 'proc', max: 1 }),
  'spec.jam': smp('cast.fail', 5, 'world', { synth: 'cast.fail', max: 1 }),
  'spec.highnoon': smp('telegraph', 6, 'tonal', { synth: 'nova', gain: 0.7, max: 1 }),
  // Grovewarden: blooms are the big heal, buds a soft chime, roots a wood crack.
  'spec.bloom': smp('heal.big', 4, 'tonal', { synth: 'heal.big', max: 2 }),
  'spec.bud': smp('heal', 1, 'tonal', { synth: 'heal', gain: 0.4, max: 1, gap: 0.25 }),
  'spec.thorn': smp('hit.venom', 3, 'spell', { synth: 'hit.venom', gain: 0.8, max: 2 }),
  'spec.roots': smp('gather.chop', 4, 'world', { synth: 'hit.earth', max: 2 }),
  // Beastbound: bites and mauls (blade hits), the howl (a creature voice), the herd (earth).
  'spec.bite': smp('hit.blade', 3, 'hit', { synth: 'hit.blade', gain: 0.8, max: 3 }),
  'spec.maul': smp('hit.blade.heavy', 5, 'hit', { synth: 'hit.blade.heavy', max: 2 }),
  'spec.howl': smp('mob.aggro', 4, 'creature', { max: 1, gap: 0.5 }),
  'spec.herd': smp('hit.earth', 5, 'hit', { synth: 'hit.earth', max: 2 }),
  dodge: both('dodge', 5, 'whoosh'),
  'cast.charge': smp('cast.charge', 2, 'spell', { max: 1 }),
  'cast.charge.storm': smp('cast.charge.storm', 2, 'spell', { max: 1 }),
  'cast.charge.fire': smp('cast.charge.fire', 2, 'spell', { max: 1 }),
  'cast.fail': both('cast.fail', 4, 'world', { gap: 0.15 }),
  resurrect: fx(5, 'fixed', { sample: 'heal.big', synth: 'heal.big' }),
  // --- movement: one sampled set per surface (6-10 single footfalls sliced from TomMusic's footstep
  //     files, spec.mjs; the soft -30 LUFS `foot` role) ------------------------------------------------
  step: step('stone'),
  'step.stone': step('stone'),
  'step.dirt': step('dirt'),
  'step.wood': step('wood'),
  'step.carpet': step('carpet'),
  'step.grass': step('grass'),
  'step.gravel': step('gravel'),
  'step.wet': step('wet'),
  'move.jump': both('move.jump', 2, 'step', { max: 2, gap: 0.08 }),
  'move.land': both('move.land', 2, 'step', { max: 2, gap: 0.08 }),
  // +glide: the canopy's cloth "fwump" (the Dodge whoosh, played low), its fold (a light whoosh), a
  // soft landing from a glide (the landing thump, fuller). The flight wind is a loop (audio/glideAudio.ts).
  'glide.open': smp('dodge', 4, 'whoosh', { synth: 'move.jump', max: 1, gap: 0.15, gain: 0.95 }),
  'glide.fold': smp('swing.light', 2, 'whoosh', { synth: 'move.jump', max: 1, gap: 0.15, gain: 0.5 }),
  'glide.land': smp('move.land', 3, 'step', { synth: 'move.land', max: 1, gap: 0.2, gain: 1.15 }),
  // --- danger ----------------------------------------------------------------------------------
  hurt: both('hurt', 6, 'hit', { duck: 0.6 }),
  'enemy.hit': both('enemy.hit', 1, 'hit'),
  slam: both('slam', 6, 'hit', { max: 2 }),
  /** Boss telegraph warning: a tritone steel bell over a reversed rumble (never pitched much). */
  telegraph: both('telegraph', 7, 'tonal', { max: 2, gap: 0.12, duck: 0.55 }),
  'enemy.cast': smp('enemy.cast', 1, 'spell', { max: 2, gap: 0.1 }),
  'mob.aggro': smp('mob.aggro', 3, 'creature', { max: 1, gap: 0.6 }),
  'mob.death': smp('mob.death', 1, 'creature', { max: 2, gap: 0.08 }),
  'boss.roar': smp('boss.roar', 7, 'creature', { max: 1, gap: 2, duck: 0.5, pitch: 0.02 }),
  'death.self': smp('death.self', 8, 'fixed', { max: 1, gap: 1, duck: 0.35 }),
  'death.party': smp('death.party', 5, 'tonal', { max: 1, gap: 0.5 }),
  /** Low-health heartbeat (two thumps). */
  heartbeat: both('heartbeat', 6, 'fixed', { max: 1, pitch: 0.01, vary: 0.04 }),
  // --- world -------------------------------------------------------------------------------------
  'object.hit': smp('object.hit', 2, 'world', { max: 2 }),
  'object.break': smp('object.break', 3, 'world', { max: 2 }),
  'chest.open': smp('chest.open', 4, 'world', { max: 1, pitch: 0.01 }),
  'chest.close': smp('chest.close', 3, 'world', { max: 1 }),
  pickup: smp('pickup', 3, 'world', { max: 2, gap: 0.06 }),
  'door.open': smp('door.open', 3, 'world', { max: 1, gap: 0.5 }),
  'door.close': smp('door.close', 4, 'world', { max: 1, gap: 0.5 }),
  'gate.open': smp('gate.open', 3, 'world', { max: 1, gap: 0.5 }),
  'gate.close': smp('gate.close', 4, 'world', { max: 1, gap: 0.5 }),
  'lock.unlock': smp('lock.unlock', 3, 'world', { max: 1, gap: 0.3 }),
  /** Gathering at resource nodes (trees / ore) and the mine. */
  'gather.chop': smp('gather.chop', 2, 'world', { max: 2, gap: 0.12 }),
  'gather.mine': smp('gather.mine', 2, 'world', { max: 2, gap: 0.12 }),
  /** +prof: plucking herbs: short, brightened TomMusic 'Dirt Walk' rustles (spec.mjs gather.herb). */
  'gather.herb': smp('gather.herb', 2, 'world', { max: 2, gap: 0.18, pitch: 0.08 }),
  'torch.light': smp('torch.light', 2, 'world', { max: 1, gap: 0.3 }),
  // --- interface -----------------------------------------------------------------------------------
  'ui.click': ui('ui.click', { gap: 0.03, max: 2, pitch: 0.04 }),
  'ui.hover': ui('ui.hover', { gap: 0.06, max: 1, prio: 1 }),
  'ui.tab': ui('ui.tab', { gap: 0.04, max: 1 }),
  'ui.open': ui('ui.open', { gap: 0.08, max: 1 }),
  'ui.close': ui('ui.close', { gap: 0.08, max: 1 }),
  'ui.confirm': ui('ui.confirm', { gap: 0.08, max: 1, ...R.tonal }),
  /** "Not enough mana" / "Out of range" (hud/uiErrors.ts, rate-limited). */
  'ui.error': ui('ui.error', { synth: 'ui.error', gap: 0.25, max: 1, prio: 3 }),
  'ui.chat': ui('ui.chat', { gap: 0.15, max: 1, ...R.tonal }),
  'ui.tick': ui('ui.tick', { gap: 0.04, max: 2 }),
  /** One steel chime the meta cue notes are pitched from (ui.ts playUiCue). Exact pitch: no jitter. */
  'ui.chime': ui('ui.chime', { max: 8, pitch: 0, vary: 0.02 }),
  // --- rewards / progression / social (meta sprite) ---------------------------------------------------
  'party.invite': meta('party.invite', { gap: 0.5 }),
  'party.join': meta('party.join', { gap: 0.3 }),
  'party.leave': meta('party.leave', { gap: 0.3 }),
  'party.ragequit': meta('party.ragequit', { gap: 0.5, ...R.world }),
  'guild.recruit': meta('guild.recruit', { gap: 0.3 }),
  'idle.claim': meta('idle.claim', { gap: 0.3 }),
  'idle.tick': meta('idle.tick', { gap: 0.05, max: 2, pitch: 0.08 }),
  'loot.gear': meta('loot.gear', { gap: 0.05, max: 2, ...R.world }),
  'loot.gem': meta('loot.gem', { gap: 0.05, max: 2 }),
  'loot.bell': meta('loot.bell', { gap: 0.1, max: 1 }),
  'forge.hammer': meta('forge.hammer', { gap: 0.05, max: 2, ...R.hit }),
  'forge.fail': meta('forge.fail', { gap: 0.1, max: 1 }),
  'key.tick': ui('ui.tick', { gain: 1.6, gap: 0.2, max: 1, pitch: 0 }),
  'key.go': meta('key.go', { max: 1, gap: 1, ...R.fixed }),
  'boss.defeat': meta('boss.defeat', { bus: 'music', max: 1, gap: 2, duck: 0.6, ...R.fixed }),
  'key.timed': meta('key.timed', { bus: 'music', max: 1, gap: 3, duck: 0.5, ...R.fixed }),
  'key.depleted': meta('key.depleted', { bus: 'music', max: 1, gap: 3, duck: 0.5, ...R.fixed }),
  'level.up': meta('level.up', { bus: 'music', max: 1, gap: 1, duck: 0.6, ...R.fixed }),
  wipe: meta('wipe', { bus: 'music', max: 1, gap: 3, ...R.fixed }),
  // +field: the renderer plays `level.impact` at the level-up pillar's burst (world sfx, ducks the
  // rest); the HUD plays `bounty.complete` on a bounty turn-in.
  'level.impact': both('level.impact', 8, 'fixed', { max: 1, gap: 0.4, duck: 0.5 }),
  'bounty.complete': meta('bounty.complete', { synth: 'bounty.complete', max: 1, gap: 0.5 }),
  // +field: reward pops landing in the HUD (hud/field/rewardPops.ts).
  'reward.xp': ui('reward.xp', { synth: 'reward.xp', gap: 0.06, max: 2, pitch: 0.03 }),
  'reward.coin': ui('reward.coin', { synth: 'reward.coin', gap: 0.06, max: 2, pitch: 0.06 }),
  'reward.item': meta('reward.item', { synth: 'reward.item', gap: 0.1, max: 1 }),
  'reward.rare': meta('reward.rare', { synth: 'reward.rare', gap: 0.2, max: 1, pitch: 0 }),
};

/** Sound ids the rest of the game may play (typed helper for hooks). */
export type SoundId = keyof typeof SOUNDS;

/** Variant clip keys per sample base id, from the manifest ("hit.blade" → ["hit.blade#0", …]). */
export function variantsOf(sample: string, clips: Readonly<Record<string, unknown>> = AUDIO_MANIFEST.clips): string[] {
  const out: string[] = [];
  for (let i = 0; ; i++) {
    const k = `${sample}#${i}`;
    if (!(k in clips)) break;
    out.push(k);
  }
  return out;
}

/** Sprite groups each id needs (for preloading by screen). */
export function groupOf(id: string): string | null {
  return SOUNDS[id]?.group ?? null;
}

/**
 * Meta UI cue ids (meta/audio/sfx.ts `keystone:sfx`, e.g. 'sfx.loot.epic') → sampled layers
 * played under that cue's synthesized chime. Exact id first, else the longest '.'-ending prefix.
 */
export const UI_CUE_LAYERS: Readonly<Record<string, readonly string[]>> = {
  'sfx.loot.common': ['loot.gear'],
  'sfx.loot.uncommon': ['loot.gear'],
  'sfx.loot.rare': ['loot.gear', 'loot.gem'],
  'sfx.loot.epic': ['loot.gem'],
  'sfx.loot.legendary': ['loot.gem', 'loot.bell'],
  'sfx.loot.discover': ['loot.gem'],
  'sfx.offering.': ['loot.gem'],
  'sfx.forge.tap': ['forge.hammer'],
  'sfx.forge.nearmiss': ['forge.fail'],
  'sfx.reveal.common': ['loot.gear'],
  'sfx.reveal.uncommon': ['loot.gear'],
  'sfx.reveal.rare': ['loot.gem'],
  'sfx.reveal.epic': ['loot.gem', 'loot.bell'],
  'sfx.reveal.legendary': ['loot.gem', 'loot.bell'],
  'sfx.temper.start': ['forge.hammer'],
  'sfx.temper.success': ['forge.hammer'],
  'sfx.temper.surge': ['forge.hammer', 'loot.bell'],
  'sfx.temper.fail_soft': ['forge.fail'],
  'sfx.temper.nearmiss': ['forge.fail'],
  'sfx.set.bonus': ['loot.bell'],
  'sfx.awaken.': ['loot.bell'],
};

export function uiCueLayers(id: string): readonly string[] {
  const exact = UI_CUE_LAYERS[id];
  if (exact) return exact;
  let best: string | null = null;
  for (const k of Object.keys(UI_CUE_LAYERS)) if (k.endsWith('.') && id.startsWith(k) && (!best || k.length > best.length)) best = k;
  return best ? UI_CUE_LAYERS[best]! : [];
}
